/**
 * Iter Feb2026 · ProjectPhasesStrip
 *
 * Horizontal chip row of a project's phases with per-chip task counts.
 * Sits at the top of the Project → Tasks sub-tab. Also owns the "Group
 * by phase" toggle for the underlying task list, and the "Require
 * approval before execution" project switch when the caller has admin
 * powers.
 *
 * Contract:
 *   • wsId, projectId — required.
 *   • activePhaseId — controlled filter; null shows all phases, "unphased"
 *     shows only tasks with no phase, or a phase id to show one phase.
 *   • onActivePhaseChange(id) — parent updates client-side filter.
 *   • groupByPhase, onGroupByPhaseChange — controls the AdvancedTable
 *     groupBy prop on the tasks table.
 *   • onPhasesLoaded(phases[]) — parent gets a copy so it can hydrate
 *     `phase_name` on task rows and pass phases to the task-create form.
 *   • canEdit — gate the "+ New phase", rename, delete, and approval toggle.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Layers, Pencil, Trash2, ShieldCheck, X, Check, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import PhaseTemplatesDialog from "@/components/project/PhaseTemplatesDialog";

const PHASE_COLORS = [
  { label: "Violet",  value: "#7c3aed" },
  { label: "Cyan",    value: "#0891b2" },
  { label: "Amber",   value: "#d97706" },
  { label: "Emerald", value: "#059669" },
  { label: "Rose",    value: "#e11d48" },
  { label: "Slate",   value: "#475569" },
];

export default function ProjectPhasesStrip({
  wsId, projectId,
  activePhaseId = null,
  onActivePhaseChange,
  groupByPhase = false,
  onGroupByPhaseChange,
  onPhasesLoaded,
  canEdit = false,
}) {
  const [phases, setPhases] = useState([]);
  const [unphased, setUnphased] = useState({ total: 0, open: 0, done: 0 });
  const [requireApproval, setRequireApproval] = useState(false);
  const [autoApprove, setAutoApprove] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);   // phase being edited
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const r = await api.get(`/projects/${projectId}/phases`);
      const list = r.data.phases || [];
      setPhases(list);
      setUnphased(r.data.unphased_stats || { total: 0, open: 0, done: 0 });
      setRequireApproval(!!r.data.require_task_approval);
      setAutoApprove(r.data.auto_approve_below || null);
      onPhasesLoaded?.(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load phases");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const totalTasks = useMemo(() => {
    const inPhases = phases.reduce((s, p) => s + (p.stats?.total || 0), 0);
    return inPhases + (unphased.total || 0);
  }, [phases, unphased]);

  const hasAnyPhase = phases.length > 0;

  const move = async (phid, dir) => {
    const idx = phases.findIndex((p) => p.id === phid);
    if (idx < 0) return;
    const to = idx + dir;
    if (to < 0 || to >= phases.length) return;
    const next = phases.slice();
    const [row] = next.splice(idx, 1);
    next.splice(to, 0, row);
    setPhases(next);
    try {
      await api.patch(`/projects/${projectId}/phases/reorder`, {
        phase_ids: next.map((p) => p.id),
      });
    } catch (e) {
      toast.error("Failed to reorder");
      load();
    }
  };

  return (
    <div className="border border-border bg-muted/10" data-testid="project-phases-strip">
      <div className="px-2 py-1 flex items-center gap-1.5 flex-nowrap">
        {/* Scrollable chip zone — stays in one row, horizontal scroll on overflow. */}
        <div
          className="flex-1 min-w-0 flex items-center gap-1.5 flex-nowrap overflow-x-auto whitespace-nowrap scrollbar-thin"
          data-testid="project-phases-chip-scroll">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
            <Layers className="w-3 h-3" /> Phases
          </div>
          {/* "All" chip */}
          <button
            type="button"
            onClick={() => onActivePhaseChange?.(null)}
            className={"inline-flex items-center gap-1 h-7 px-2 border text-[11px] rounded-none shrink-0 " +
              (activePhaseId === null ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-muted")}
            data-testid="project-phases-chip-all">
            All <span className="font-mono tabular-nums opacity-70">·{totalTasks}</span>
          </button>
          {/* Phase chips */}
          {phases.map((ph) => {
            const active = activePhaseId === ph.id;
            const stats = ph.stats || {};
            return (
              <button
                key={ph.id}
                type="button"
                onClick={() => onActivePhaseChange?.(ph.id)}
                className={"group inline-flex items-center gap-1.5 h-7 px-2 border text-[11px] rounded-none shrink-0 " +
                  (active ? "border-primary bg-primary/10 font-semibold" : "border-border hover:bg-muted")}
                style={active && ph.color ? { boxShadow: `inset 0 0 0 1px ${ph.color}` } : {}}
                data-testid={`project-phases-chip-${ph.id}`}
                title={ph.description || ph.name}>
                {ph.color && (
                  <span className="w-1.5 h-1.5 shrink-0" style={{ background: ph.color }} />
                )}
                <span className="truncate max-w-[14ch]">{ph.name}</span>
                <span className="font-mono tabular-nums opacity-70">·{stats.total || 0}</span>
                {(stats.pending_approval || 0) > 0 && (
                  <span className="font-mono tabular-nums text-amber-700 bg-amber-500/10 border border-amber-500/40 px-1 leading-none">
                    {stats.pending_approval}⏳
                  </span>
                )}
                {canEdit && (
                  <span className="opacity-0 group-hover:opacity-70 inline-flex items-center gap-0.5">
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); setEditing(ph); }}
                      className="p-0.5 hover:text-foreground"
                      data-testid={`project-phases-edit-${ph.id}`}
                      title="Rename / edit">
                      <Pencil className="w-2.5 h-2.5" />
                    </span>
                  </span>
                )}
              </button>
            );
          })}
          {/* Unphased chip (only when there are any) */}
          {(unphased.total > 0) && (
            <button
              type="button"
              onClick={() => onActivePhaseChange?.("unphased")}
              className={"inline-flex items-center gap-1 h-7 px-2 border text-[11px] rounded-none italic shrink-0 " +
                (activePhaseId === "unphased" ? "border-primary bg-primary/10 text-primary font-semibold" : "border-dashed border-border text-muted-foreground hover:bg-muted")}
              data-testid="project-phases-chip-unphased">
              Unphased <span className="font-mono tabular-nums opacity-70">·{unphased.total}</span>
            </button>
          )}
          {/* + New phase */}
          {canEdit && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1 h-7 px-2 border border-dashed border-border hover:bg-muted text-[11px] rounded-none text-muted-foreground shrink-0"
              data-testid="project-phases-new"
              title="New phase">
              <Plus className="w-3 h-3" />
            </button>
          )}
          {/* Iter Feb2026 · Phase templates — apply / save-as. */}
          {canEdit && (
            <div className="shrink-0">
              <PhaseTemplatesDialog
                wsId={wsId}
                projectId={projectId}
                phaseCount={phases.length}
                onApplied={load}
              />
            </div>
          )}
        </div>
        {/* Right-side controls — pinned, never scroll */}
        <div className="flex items-center gap-3 shrink-0 pl-2 border-l border-border/60">
          {/* Group-by-phase toggle */}
          {hasAnyPhase && (
            <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer" title="Group tasks by phase">
              <Switch
                checked={groupByPhase}
                onCheckedChange={(v) => onGroupByPhaseChange?.(v)}
                data-testid="project-phases-groupby-toggle"
              />
              <span className="hidden md:inline">Group by phase</span>
            </label>
          )}
          {/* Approval toggle */}
          {canEdit && (
            <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer"
                   title="When on, every new task lands as 'pending' with no assignee until an approver signs off.">
              <Switch
                checked={requireApproval}
                onCheckedChange={async (v) => {
                  setRequireApproval(v);
                  try {
                    await api.patch(`/projects/${projectId}/require-task-approval`, {
                      require_task_approval: v,
                      auto_approve_below: autoApprove,
                    });
                    toast.success(v ? "Approval gate ON · new tasks need approval" : "Approval gate OFF");
                  } catch (e) {
                    setRequireApproval(!v);
                    toast.error(e?.response?.data?.detail || "Failed");
                  }
                }}
                data-testid="project-phases-require-approval-toggle"
              />
              <ShieldCheck className="w-3 h-3 opacity-60" />
              <span className="hidden md:inline">Require approval</span>
            </label>
          )}
        </div>
      </div>
      {/* Movement handles when editing enabled */}
      {canEdit && phases.length > 1 && (
        <div className="px-3 pb-2 flex items-center gap-1 text-[10px] text-muted-foreground flex-nowrap overflow-x-auto whitespace-nowrap">
          <span className="uppercase tracking-widest shrink-0">Reorder:</span>
          {phases.map((ph, idx) => (
            <span key={ph.id} className="inline-flex items-center gap-0.5 border border-border px-1 py-0.5 shrink-0"
                  data-testid={`project-phases-reorder-${ph.id}`}>
              <GripVertical className="w-2.5 h-2.5 opacity-40" />
              <span className="truncate max-w-[12ch]">{ph.name}</span>
              <button type="button" disabled={idx === 0}
                      onClick={() => move(ph.id, -1)}
                      className="p-0.5 hover:text-foreground disabled:opacity-30"
                      data-testid={`project-phases-reorder-up-${ph.id}`}>
                <ArrowUp className="w-2.5 h-2.5" />
              </button>
              <button type="button" disabled={idx === phases.length - 1}
                      onClick={() => move(ph.id, 1)}
                      className="p-0.5 hover:text-foreground disabled:opacity-30"
                      data-testid={`project-phases-reorder-down-${ph.id}`}>
                <ArrowDown className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Create / Edit dialogs */}
      <PhaseDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSaved={load}
        projectId={projectId}
      />
      <PhaseDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSaved={load}
        onDelete={() => setConfirmDelete(editing)}
        projectId={projectId}
        phase={editing}
      />
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="rounded-none" data-testid="project-phases-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete phase &ldquo;{confirmDelete?.name}&rdquo;?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Tasks in this phase will move to <b>Unphased</b> — they are not deleted.
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={async () => {
                      try {
                        await api.delete(`/projects/${projectId}/phases/${confirmDelete.id}`);
                        toast.success("Phase deleted · tasks unphased");
                        setConfirmDelete(null); setEditing(null);
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Failed");
                      }
                    }}
                    data-testid="project-phases-delete-confirm-btn">
              Delete phase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhaseDialog({ open, onOpenChange, onSaved, projectId, phase, onDelete }) {
  const isEdit = !!phase;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(PHASE_COLORS[0].value);
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(phase?.name || "");
      setDescription(phase?.description || "");
      setColor(phase?.color || PHASE_COLORS[0].value);
      setBudget(phase?.budget_estimate != null ? String(phase.budget_estimate) : "");
    }
  }, [open, phase]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        color,
        budget_estimate: budget ? Number(budget) : null,
      };
      if (isEdit) {
        await api.patch(`/projects/${projectId}/phases/${phase.id}`, body);
        toast.success("Phase updated");
      } else {
        await api.post(`/projects/${projectId}/phases`, body);
        toast.success("Phase created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none" data-testid="project-phases-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit phase" : "New phase"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Name</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Discovery, Design, Launch"
              className="rounded-none mt-1"
              autoFocus
              data-testid="project-phases-dialog-name"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Description (optional)</Label>
            <Input
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What happens in this phase"
              className="rounded-none mt-1"
              data-testid="project-phases-dialog-description"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Color</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {PHASE_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={"h-6 w-6 border-2 " + (color === c.value ? "border-foreground" : "border-transparent")}
                  style={{ background: c.value }}
                  title={c.label}
                  data-testid={`project-phases-dialog-color-${c.value.slice(1)}`}>
                  {color === c.value && <Check className="w-3 h-3 text-white mx-auto" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Budget estimate (optional)</Label>
            <Input
              value={budget} onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="e.g. 25000"
              inputMode="decimal"
              className="rounded-none mt-1"
              data-testid="project-phases-dialog-budget"
            />
          </div>
        </div>
        <DialogFooter>
          {isEdit && onDelete && (
            <Button variant="outline" className="rounded-none mr-auto text-rose-700 border-rose-500/60 hover:bg-rose-500/10"
                    onClick={() => { onDelete(); }}
                    data-testid="project-phases-dialog-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="project-phases-dialog-save">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create phase")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
